import { createContext, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';

import type { UserType } from '../User';

type OverrideProviderProps = { children: React.ReactNode };

export type OverrideProviderContextValue = ReturnType<typeof useAuth0> & {
  getUser?: () => Promise<UserType>;
  isEmbedded?: boolean;
};

export const OverrideContext = createContext<
  OverrideProviderContextValue | undefined
>(undefined);

const tryGetUser = (token: string) => {
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

const storeName = 'override-session-token';
let updateToken: (token: string) => void = () => {};

export const OverrideProvider: React.FC<OverrideProviderProps> = ({
  children,
}) => {
  const [token, setToken] = useState(
    () =>
      sessionStorage.getItem(storeName) ||
      process.env.REACT_APP_OVERRIDE_TOKEN ||
      ''
  );
  updateToken = setToken;

  const user = useMemo(() => (token ? tryGetUser(token) : null), [token]);

  const overrideValue: OverrideProviderContextValue = useMemo(
    () => ({
      user: user!,
      isLoading: !token,
      isAuthenticated: !!token,
      loginWithRedirect: async () => {},
      getIdTokenClaims: async () => {
        return { __raw: token };
      },
      logout: async () => {},
      loginWithPopup: async () => {},
      getAccessTokenWithPopup: async () => {
        return token;
      },
      getUser: async () => user as UserType,
      handleRedirectCallback: async () => {
        return {};
      },
      getAccessTokenSilently: async () => token as any,
    }),
    [user, token]
  );

  if (!user) return children;

  return (
    <OverrideContext.Provider value={overrideValue}>
      {children}
    </OverrideContext.Provider>
  );
};

window.addEventListener('message', event => {
  const data = event.data;
  if (data && data.type === storeName && data.token) {
    sessionStorage.setItem(storeName, data.token);
    return updateToken(data.token);
  }
});
